import getCookieConsent from './getCookieConsent';
import { normalizePhoneNumber } from './normalizePhoneNumber';
import {
  ProductDetailsType,
  RebateCampaignDetails,
} from 'types/ProductDetailsType';
// Defined here rather than in logGtmEvent so the dependency stays one-way
// (logGtmEvent -> buildRebateEventData) and the two never form an import cycle.
export type RebateEventData = {
  customerData: Record<string, any>;
  rebateData: Record<string, any>;
  rebateCampaignData: Record<string, any>;
  experienceData: Record<string, any>;
};

// Receipt metadata the BROWSER genuinely has. All receipt parsing (vendor,
// total, tax, line items) happens server-side in rebates-service after upload
// and never comes back to the client, so the most a submission event can carry
// is the file identity and the S3 URLs it just produced.
export type RebateReceiptFile = {
  fileName?: string;
  fileUrl?: string;
  originalFileUrl?: string;
  fileType?: string;
  fileSize?: number;
};

export type BuildRebateEventDataArgs = {
  productDetails?: ProductDetailsType | null;
  rebateId?: string | null;
  rebateStatus: 'REBATE_INITIATION' | 'REBATE_SUBMISSION';
  // 'Phone Number Entry' when the shopper typed a number into the opt-in form,
  // 'Keyword' when they were deep-linked into their SMS app to text in. These
  // are the two values the rebateSignUpMethod event field is defined with
  // (global-events-watcher/helper/getRebateData.js).
  rebateSignUpMethod?: 'Phone Number Entry' | 'Keyword';
  email?: string | null;
  phoneNumber?: string | null;
  emailConsent?: boolean | null;
  smsConsent?: boolean | null;
  receipts?: RebateReceiptFile[];
};

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

// Mirrors the registration-submission builder: emit every populated field from
// experienceDetails and drop the empty ones, so the two event families describe
// an experience the same way.
const buildExperienceData = (
  productDetails?: ProductDetailsType | null
): Record<string, any> => {
  const experienceDetails = productDetails?.product?.experienceDetails;
  const experienceData: Record<string, any> = {};
  if (experienceDetails) {
    for (const [key, value] of Object.entries(experienceDetails)) {
      if (hasValue(value)) experienceData[key] = value;
    }
  }
  if (!experienceData.experienceImage && productDetails?.product?.image) {
    experienceData.experienceImage = productDetails.product.image;
  }
  return experienceData;
};

// Campaign detail is served by consumer-app-microservice already shaped with
// the rebateCampaignData field names, so it passes straight through; empty
// fields are dropped rather than sent as nulls.
const buildRebateCampaignData = (
  rebateCampaign?: RebateCampaignDetails | null
): Record<string, any> => {
  const campaignData: Record<string, any> = {};
  if (rebateCampaign) {
    for (const [key, value] of Object.entries(rebateCampaign)) {
      if (hasValue(value)) campaignData[key] = value;
    }
  }
  return campaignData;
};

const buildRebateEventData = (
  args: BuildRebateEventDataArgs
): RebateEventData => {
  const customerData: Record<string, any> = {};

  if (hasValue(args.email)) customerData.customerEmail = args.email;

  const normalizedPhone = normalizePhoneNumber(args.phoneNumber);
  if (normalizedPhone) customerData.customerPhoneNumber = normalizedPhone;

  if (args.emailConsent !== null && args.emailConsent !== undefined) {
    customerData.customerEmailConsent = args.emailConsent;
  }
  if (args.smsConsent !== null && args.smsConsent !== undefined) {
    customerData.customerSMSSingleConsent = args.smsConsent;
  }

  // Consent snapshot resolution reads brand.cookieConsentSettings; calling it
  // before product details load would report the geo-exempt all-granted tier.
  // Every rebate event fires from a user action on a loaded experience, so the
  // settings are present by then.
  const cookieConsent = getCookieConsent(
    args.productDetails?.brand?.cookieConsentSettings
  );
  if (cookieConsent) customerData.cookieConsent = cookieConsent;

  const rebateData: Record<string, any> = {
    rebateStatus: args.rebateStatus,
  };
  if (hasValue(args.rebateId)) rebateData.rebateId = args.rebateId;
  if (args.rebateSignUpMethod) {
    rebateData.rebateSignUpMethod = args.rebateSignUpMethod;
  }

  const receipts = (args.receipts || []).filter((receipt) =>
    hasValue(receipt?.fileUrl)
  );
  if (receipts.length) {
    // rebateReceipt matches the server's single-URL Rebate Receipt field;
    // rebateReceiptFiles carries the per-file detail the browser also knows.
    rebateData.rebateReceipt = receipts[0].fileUrl;
    rebateData.rebateReceiptFiles = receipts.map((receipt) => {
      const file: Record<string, any> = {};
      if (hasValue(receipt.fileName)) file.fileName = receipt.fileName;
      if (hasValue(receipt.fileUrl)) file.fileUrl = receipt.fileUrl;
      if (hasValue(receipt.originalFileUrl)) {
        file.originalFileUrl = receipt.originalFileUrl;
      }
      if (hasValue(receipt.fileType)) file.fileType = receipt.fileType;
      if (hasValue(receipt.fileSize)) file.fileSize = receipt.fileSize;
      return file;
    });
    rebateData.rebateReceiptCount = receipts.length;
  }

  return {
    experienceData: buildExperienceData(args.productDetails),
    customerData,
    rebateData,
    rebateCampaignData: buildRebateCampaignData(
      args.productDetails?.product?.rebateCampaign
    ),
  };
};

export default buildRebateEventData;
